'use client'

/**
 * AverageDownCalculator: free tool for "average down calculator / stock average calculator / cost basis
 * calculator". Two (or one plus a new) buys → blended average cost and total position. Optionally add a
 * current price to see unrealized P/L and the percentage move back to breakeven. Pure client math.
 */

import { useMemo, useState } from 'react'
import CalcField from '@/components/calc/CalcField'

const fmtUSD = (n: number): string => (isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }) : '$0.00')
const fmtUSD2 = (n: number): string => (isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) : '$0.00')
const fmtPct = (n: number): string => `${isFinite(n) && n >= 0 ? '+' : ''}${isFinite(n) ? n.toFixed(2) : '0.00'}%`

export default function AverageDownCalculator() {
  const [sh1, setSh1] = useState('100')
  const [px1, setPx1] = useState('10.00')
  const [sh2, setSh2] = useState('100')
  const [px2, setPx2] = useState('7.00')
  const [current, setCurrent] = useState('')

  const r = useMemo(() => {
    const s1 = parseFloat(sh1) || 0
    const p1 = parseFloat(px1) || 0
    const s2 = parseFloat(sh2) || 0
    const p2 = parseFloat(px2) || 0
    const cur = parseFloat(current)
    const totalShares = s1 + s2
    const totalCost = s1 * p1 + s2 * p2
    const avg = totalShares > 0 ? totalCost / totalShares : 0
    const hasCurrent = isFinite(cur) && cur > 0
    const marketValue = hasCurrent ? cur * totalShares : 0
    const pnl = hasCurrent ? marketValue - totalCost : 0
    const pnlPct = hasCurrent && totalCost > 0 ? (pnl / totalCost) * 100 : 0
    const toBreakeven = hasCurrent && cur > 0 ? ((avg - cur) / cur) * 100 : 0 // % move up needed to reach avg
    const invalid = totalShares <= 0 || totalCost <= 0
    return { totalShares, totalCost, avg, hasCurrent, marketValue, pnl, pnlPct, toBreakeven, invalid }
  }, [sh1, px1, sh2, px2, current])

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <CalcField label="First buy: shares" value={sh1} onChange={setSh1} step="1" hint="Your original position" />
        <CalcField label="First buy: price" value={px1} onChange={setPx1} prefix="$" />
        <CalcField label="Second buy: shares" value={sh2} onChange={setSh2} step="1" hint="The shares you are adding" />
        <CalcField label="Second buy: price" value={px2} onChange={setPx2} prefix="$" />
        <CalcField label="Current price (optional)" value={current} onChange={setCurrent} prefix="$" hint="Add it to see P/L and % to breakeven" />
      </div>

      {r.invalid ? (
        <div className="mt-5 rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-6 text-center text-sm text-zinc-400">
          Enter your two buys to get the new average cost.
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.07] px-5 py-4">
              <div className="text-[12px] font-bold uppercase tracking-wider text-emerald-300">New average cost</div>
              <div className="mt-1 text-[34px] font-black leading-none tabular-nums text-white">{fmtUSD(r.avg)}</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">{r.totalShares.toLocaleString('en-US')} shares · {fmtUSD2(r.totalCost)} total cost</div>
            </div>
            {r.hasCurrent ? (
              <div className="rounded-2xl border border-zinc-700/70 bg-zinc-900/60 px-5 py-4">
                <div className="text-[12px] font-bold uppercase tracking-wider text-zinc-400">At the current price</div>
                <div className="mt-1.5 space-y-1 text-[14px] tabular-nums text-zinc-300">
                  <div className="flex justify-between"><span className="text-zinc-500">Unrealized P/L</span><span className="font-bold" style={{ color: r.pnl >= 0 ? '#34d399' : '#f43f5e' }}>{fmtUSD2(r.pnl)} ({fmtPct(r.pnlPct)})</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Position value</span><span className="font-bold">{fmtUSD2(r.marketValue)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Move to breakeven</span><span className="font-bold">{r.toBreakeven > 0 ? fmtPct(r.toBreakeven) : 'already above'}</span></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center rounded-2xl border border-dashed border-zinc-700/70 bg-zinc-900/40 px-5 py-4 text-[13px] text-zinc-500">
                Add a current price above to see your unrealized profit or loss and how far the stock must move to break even at the new average.
              </div>
            )}
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-zinc-600">
            Averaging down raises your share count and lowers your average cost, but it also adds risk to a losing position. This is an educational tool, not investment advice.
          </p>
        </>
      )}
    </div>
  )
}
